import { loadScriptPromise } from './DOMHelper';
import { PDF_JS_CDN } from '~/config/external-packages';
import { PDF_PASSWORD_STATUS } from '../constants';

/**
 * Converts a PDF file into an array of image data URLs, with each page rendered as an image.
 *
 * @param {File} file - The PDF file to convert. This must be a valid File object with a MIME type of `application/pdf`.
 * @returns {Promise<string[]>} - A promise that resolves to an array of image data URLs, where each URL represents a page in the PDF.
 *
 * @throws {Error} If the PDF file cannot be processed or if the page count is invalid.
 *
 * @description
 * The function uses the browser's built-in PDF rendering capabilities via an `<iframe>`. Each page of the PDF is captured 
 * as a rendered image using an offscreen `<canvas>` element. The resulting images are returned as data URLs in an array.
 *
 * Note:
 * - The user is prompted to manually enter the number of pages in the PDF.
 * - A short delay (`setTimeout`) is applied to ensure rendering completes before capturing each page.
 */
export async function pdfToImages({ file, url, id, password }) {
    try {
        // Dynamically load pdf.js only when the function is called
        if (typeof window.pdfjsLib === 'undefined') {
            // Dynamically load the pdf.js library from CDN
            await loadScriptPromise(PDF_JS_CDN, 'pdfjs');
        }
        /**
         * Most servers that hosts these files doesn't give access
         * to freeconvert.com client app to get/process the files
         * because of CORS policy. So for a URL file upload, we 
         * import the file to our fc server and access them from
         * our server URL. The below code rejects any URL that is
         * not from freeconvert.com origin and waits for when the 
         * import job would finish.
         */
        if(file === 'url' && !url.includes('freeconvert.com')) {            
            return [false]
        }

        const fileUrl = file instanceof File ? URL.createObjectURL(file) : url;
        // Fetch the PDF document with the provided password
        
        window[`pdf-${id}`] = await pdfjsLib.getDocument({
            url: fileUrl,
            password: password, // Pass the password here
        }).promise;

        const images = [];   

        for (let pageNum = 1; pageNum <= window[`pdf-${id}`].numPages; pageNum++) {
            if(pageNum === 1) images[0] = await extractImageFromPage(pageNum, id)
            else images.push(null)            
        }                

        $nuxt.$store.commit('updateFile', {id: id, props: {
            extracted: true,
            passwordStatus: password ? PDF_PASSWORD_STATUS.UNLOCKED : PDF_PASSWORD_STATUS.OPEN
        }})



        setTimeout(() => {autoUpdatePreview(id)}, 500)
        return images;
    } catch (e) {
        if (e.name === 'PasswordException') {
            // Handle password-related errors
            if(e.message ===  'Incorrect Password') $nuxt.$store.commit('updateFile', {id: id, props: {passwordStatus: PDF_PASSWORD_STATUS.INCORRECT}})
            else $nuxt.$store.commit('updateFile', {id: id, props: {passwordStatus: PDF_PASSWORD_STATUS.PROTECTED}})

            throw { name: e.name, message: e.message, fileId: id };
        } else {
            // Handle other errors
            throw { name: e.name, message: e.message, fileId: id };
        }
    }
}

export const extractImageFromPage = async (pageNum, fileId) => { 
    return new Promise(async (resolve, reject) => {
        try {            
            if(!window[`pdf-${fileId}`]) resolve(null)
            const page = await window[`pdf-${fileId}`].getPage(pageNum);
            const viewport = page.getViewport({ scale: 1.2 });
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');

            canvas.width = viewport.width;
            canvas.height = viewport.height;

            await page.render({ canvasContext: context, viewport: viewport }).promise;
            const image = canvas.toDataURL('image/png')
            $nuxt.$store.commit('thumb/updatePreviewOfPage', {page: pageNum, fileId, image, dimension: {width: viewport.width, height: viewport.height}})
            resolve(image);
        } catch (e) {
            reject(e)
        }
    })
};

/**
 * Creates a blank white image with the same dimensions as the input image.
 * @param {string} imageDataUrl - The data URL of the input image.
 * @returns {Promise<string>} A Promise that resolves to the data URL of the blank white image.
 */
export async function createBlankImageDataUrl(imageDataUrl) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            const { width, height } = img;

            // Create a canvas with the same dimensions
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;

            // Get the context and fill the canvas with white
            const ctx = canvas.getContext('2d');
            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(0, 0, width, height);

            // Convert the canvas to a data URL and return it
            resolve(canvas.toDataURL());
        };

        img.onerror = (e) => reject(new Error('Failed to load the image.'));
        img.src = imageDataUrl;
    });
}
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms))
const autoUpdatePreview = async (id) => {
    const pdf = window[`pdf-${id}`];
    const batchSize = 5;
    const pageNumbers = Array.from({ length: pdf.numPages }, (_, i) => i + 1);

    if (pdf.numPages <= batchSize) {
        await Promise.all(pageNumbers.map(page => extractImageFromPage(page, id)));            
    } else {
        for (let i = 0; i < pageNumbers.length; i += batchSize) {
            const batch = pageNumbers.slice(i, i + batchSize);
            await Promise.all(batch.map(page => extractImageFromPage(page, id)));
            await sleep(10); // wait 1 second after each batch
        }
    }
}



